import { Fragment } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { faLightbulbOn } from '@fortawesome/pro-regular-svg-icons';
import { faCircleNotch, faFileChartColumn } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { generateExports } from 'api/contacts/exports';
import * as Form from 'components/form';
import { GivebutterFormProvider } from 'components/form/GivebutterFormProvider';
import { useGivebutterForm } from 'components/form/useGivebutterForm';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { YEAR_OPTIONS } from 'helpers';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

type reportType = 'zip' | 'email';

interface ExportEOYReportsProps extends AlertDialog.AlertDialogRootProps {
    onSuccess?: (reportType: reportType) => void;
    onClose?: () => void;
    resource: 'contacts' | 'companies';
    template: 'contact.summary' | 'company.summary';
}

interface ReportsFormValues {
    reportSelectedYear: string;
    reportType: reportType;
    isAuthorized: boolean;
}

const ExportEOYReports = ({
    open,
    onClose,
    onSuccess,
    resource,
    template,
    ...rest
}: ExportEOYReportsProps) => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const methods = useGivebutterForm<ReportsFormValues>({
        defaultValues: {
            reportSelectedYear: YEAR_OPTIONS[1].value.toString(),
            reportType: 'zip',
            isAuthorized: false,
        },
    });

    const { handleSubmit, control, watch, path } = methods;
    const { reportType, isAuthorized } = watch();
    const queryClient = useQueryClient();
    const defaultPayload = {
        scope: 'all',
        object: 'account',
        resource,
        object_id: accountId,
        template,
        contacts: null,
    };

    const { mutate: handleExport, isPending: isGeneratingReport } = useMutation({
        mutationFn: (formValues: ReportsFormValues) => {
            const report_type = formValues.reportType;
            const year = formValues.reportSelectedYear;
            return generateExports(accountId, { ...defaultPayload, report_type, year });
        },
        onSuccess: (data, formValues: ReportsFormValues) => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactsWithFilters(accountId),
            });

            toast.info(
                <Fragment>
                    <span className="font-medium">Processing your summary!</span> It should be
                    available soon.
                </Fragment>,
            );

            onSuccess?.(formValues.reportType);
            onClose?.();
        },
        onError: (error: GivebutterAxiosError) => {
            const errorMessage = error.response?.data?.message;

            toast.error(
                <Fragment>
                    <span className="font-medium">Whoops.</span>{' '}
                    {errorMessage ?? 'Something went wrong.'}
                </Fragment>,
            );
        },
    });

    const onExportClick = (formValues: ReportsFormValues) => {
        handleExport(formValues);
    };

    return (
        <GivebutterFormProvider {...methods}>
            <AlertDialog.Root open={open} {...rest}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay />
                    <AlertDialog.Content className={'w-96'} onEscapeKeyDown={onClose}>
                        <AlertDialog.Header>
                            <div
                                className={
                                    'flex h-12 w-12 flex-col items-center justify-center rounded-full bg-gray-100 text-center align-middle text-gray-500'
                                }
                            >
                                <FontAwesomeIcon
                                    className={'flex h-5 w-5'}
                                    icon={faFileChartColumn}
                                    fill={'text-gray-500'}
                                />
                            </div>
                            <AlertDialog.Close onClick={onClose} />
                        </AlertDialog.Header>
                        <AlertDialog.Body>
                            <form onSubmit={handleSubmit(onExportClick)}>
                                <AlertDialog.Title>Year-End Summaries</AlertDialog.Title>
                                <AlertDialog.Description>
                                    <p>
                                        Download or email PDF summaries of giving activity by
                                        contact for the selected year.
                                    </p>
                                </AlertDialog.Description>
                                <div
                                    className={
                                        'mt-3 flex flex-col gap-1 rounded-lg bg-gray-100 p-4 text-sm'
                                    }
                                >
                                    <div className={'mb-1 flex items-center gap-1 text-green-600'}>
                                        <FontAwesomeIcon
                                            icon={faLightbulbOn}
                                            fill={'text-green-600'}
                                        />
                                        <p className={'font-medium'}>Tips</p>
                                    </div>
                                    <div className={'flex flex-col gap-1'}>
                                        <p className={'text-sm'}>
                                            Year-End summaries can be added as merge fields in the
                                            Engage email editor.
                                        </p>
                                        <p className={'text-sm'}>
                                            Add a custom memo to the PDFs by visiting the Settings
                                            tab of your account.
                                        </p>
                                    </div>
                                </div>
                                <p className={'mb-2 mt-4 font-medium'}>Export type</p>
                                <Form.UseFormRadioGroup
                                    name={'reportType'}
                                    defaultValue="zip"
                                    control={control}
                                    orientation={'horizontal'}
                                    variant={'card'}
                                    className={'flex-col sm:flex-col'}
                                >
                                    <RadioGroup.Item
                                        value={'zip'}
                                        className={'flex gap-x-3 p-3'}
                                        size={'sm'}
                                    >
                                        <RadioGroup.IndicatorContainer>
                                            <RadioGroup.Indicator />
                                        </RadioGroup.IndicatorContainer>
                                        <div className={'flex flex-1 flex-col'}>
                                            <Form.Label
                                                name={path('reportType')}
                                                htmlFor={'zip'}
                                                className={'font-medium'}
                                            >
                                                Download .zip File
                                            </Form.Label>
                                            <p className={'text-sm text-gray-600'}>
                                                A zip file with all summaries for download.
                                            </p>
                                        </div>
                                    </RadioGroup.Item>
                                    <RadioGroup.Item
                                        value={'email'}
                                        className={'flex gap-x-3 p-3'}
                                        size={'sm'}
                                    >
                                        <RadioGroup.IndicatorContainer>
                                            <RadioGroup.Indicator />
                                        </RadioGroup.IndicatorContainer>
                                        <div className={'flex flex-1 flex-col'}>
                                            <Form.Label
                                                name={path('reportType')}
                                                htmlFor={'email'}
                                                className={'font-medium'}
                                            >
                                                Email my contacts
                                            </Form.Label>
                                            <p className={'text-sm text-gray-600'}>
                                                Directly email PDF summaries to each contact that
                                                has at least one gift in the selected year.
                                            </p>
                                        </div>
                                    </RadioGroup.Item>
                                </Form.UseFormRadioGroup>
                                {reportType === 'email' && (
                                    <Form.Container
                                        className={
                                            'mb-2 mt-4 flex-row gap-2 rounded-xl bg-gray-50 px-3 py-4'
                                        }
                                    >
                                        <Form.UseFormCheckboxInput
                                            control={control}
                                            name={'isAuthorized'}
                                        />
                                        <div className={'flex flex-col gap-1'}>
                                            <Form.Label
                                                className={'leading-none text-black'}
                                                name={path('isAuthorized')}
                                            >
                                                I authorize Givebutter to email individual EOY
                                                summaries to all contacts of the selected year.
                                            </Form.Label>
                                        </div>
                                    </Form.Container>
                                )}
                                <p className={'mt-4 font-medium'}>Summary year</p>
                                <Form.SingleSelect control={control} name="reportSelectedYear">
                                    <SingleSelect.Trigger
                                        aria-label="Report year"
                                        disabled={isGeneratingReport}
                                    />
                                    <SingleSelect.Content>
                                        {YEAR_OPTIONS.map(({ value, label }) => (
                                            <SingleSelect.Item key={value} value={value.toString()}>
                                                <SingleSelect.ItemText>
                                                    {label}
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                        ))}
                                    </SingleSelect.Content>
                                </Form.SingleSelect>
                                <Button
                                    variant="solid"
                                    type="submit"
                                    size="md"
                                    className="mt-4 w-full"
                                    disabled={
                                        isGeneratingReport || reportType === 'email'
                                            ? !isAuthorized
                                            : false
                                    }
                                >
                                    {isGeneratingReport && (
                                        <FontAwesomeIcon
                                            className={'flex h-5 w-5 animate-spin'}
                                            icon={faCircleNotch}
                                        />
                                    )}
                                    {reportType === 'zip' ? 'Export' : 'Email'} selected year
                                </Button>
                            </form>
                        </AlertDialog.Body>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </GivebutterFormProvider>
    );
};

const ExportEOYReportsTrigger = AlertDialog.Trigger;

const Root = ExportEOYReports;
const Trigger = ExportEOYReportsTrigger;

export {
    ExportEOYReports,
    ExportEOYReportsTrigger,
    //
    Root,
    Trigger,
};
export type { ExportEOYReportsProps, reportType };
